import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Message } from '../models/Message'
import {
  submitChatRequest,
  submitChatRequestSSE,
  ChatResponse,
  createWalkLink,
  revokeWalkLink,
} from '../services/api/chat'
import { useWalkPresence } from '../hooks/useWalkPresence'
import { ENABLE_SSE_CHAT, ENABLE_STREAM_CHAT } from '../const/EndPoints'
import LocalStorageService from '../services/localStorageService'
import { User } from '../models/User'
import { UserContext } from './UserContext'
import { useSubscription } from './SubscriptionContext'
import { getPlatform } from '../utils/helper'
import { queryKeys } from '../services/queryKeys'

export interface WalkState {
  token: string
  url: string
  guestName: string | null
}

interface ChatContextValue {
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  chatStarted: boolean
  setChatStarted: React.Dispatch<React.SetStateAction<boolean>>
  submitting: boolean
  sessionId: string | null
  setSessionId: React.Dispatch<React.SetStateAction<string | null>>
  limitReached: { resetsAt?: string } | null
  setLimitReached: React.Dispatch<
    React.SetStateAction<{ resetsAt?: string } | null>
  >
  showSessionPrompt: boolean
  setShowSessionPrompt: React.Dispatch<React.SetStateAction<boolean>>
  sessionMessageCount: number
  onSubmitMessages: (newMessages: Message[]) => Promise<void>
  abortCurrentRequest: () => void
  resetChat: () => void
  walk: WalkState | null
  walkCreating: boolean
  walkError: string | null
  createWalk: () => Promise<void>
  revokeWalk: () => Promise<void>
  clearWalkError: () => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export const useChatContext = () => {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider')
  return ctx
}

const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient()
  const { user } = useContext(UserContext) ?? {}
  const localUser = LocalStorageService.getItem<User>('user')
  const { isFree } = useSubscription()

  const [messages, setMessages] = useState<Message[]>([])
  const [chatStarted, setChatStarted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(
    LocalStorageService.getItem<string>('chatSessionId')
  )
  const [limitReached, setLimitReached] = useState<{
    resetsAt?: string
  } | null>(null)
  const [showSessionPrompt, setShowSessionPrompt] = useState(false)
  const [sessionMessageCount, setSessionMessageCount] = useState(0)
  const [walk, setWalk] = useState<WalkState | null>(null)
  const [walkCreating, setWalkCreating] = useState(false)
  const [walkError, setWalkError] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  const streamBufferRef = useRef('')
  const rafRef = useRef<number>(0)

  const abortCurrentRequest = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  const resetChat = useCallback(() => {
    abortCurrentRequest()
    setMessages([])
    setChatStarted(false)
    setSubmitting(false)
    setSessionId(null)
    setSessionMessageCount(0)
    setShowSessionPrompt(false)
    LocalStorageService.removeItem('chatSessionId')
    // Walk links are session-scoped — ending the conversation ends the walk.
    // Fire-and-forget the revoke; the server will also close the stream.
    setWalk((current) => {
      if (current) {
        revokeWalkLink(current.token).catch(() => {})
      }
      return null
    })
    setWalkError(null)
  }, [abortCurrentRequest])

  const createWalk = useCallback(async () => {
    if (!sessionId) {
      setWalkError(
        'Ask Treatwise something first — then you can walk through it together.'
      )
      return
    }
    if (walkCreating) return
    setWalkCreating(true)
    setWalkError(null)
    try {
      const hostFirst = (user?.firstName ?? localUser?.firstName) || undefined
      const result = await createWalkLink(sessionId, hostFirst)
      const url =
        result.url || `${window.location.origin}/walk/${result.token}`
      setWalk({ token: result.token, url, guestName: null })
    } catch {
      setWalkError("Couldn't get your link ready. Please try again.")
    } finally {
      setWalkCreating(false)
    }
  }, [sessionId, walkCreating, user, localUser])

  const revokeWalk = useCallback(async () => {
    setWalk((current) => {
      if (current) {
        revokeWalkLink(current.token).catch(() => {})
      }
      return null
    })
  }, [])

  const clearWalkError = useCallback(() => setWalkError(null), [])

  const onSubmitMessages = useCallback(
    async (newMessages: Message[]) => {
      const currentUser = user ?? localUser
      if (!currentUser || !currentUser.userId) return

      const formData = new FormData()
      formData.append('userId', currentUser.userId)
      if (sessionId) formData.append('sessionId', sessionId)
      formData.append('platform', getPlatform())

      const messagesToAdd: Message[] = []

      newMessages.forEach((message) => {
        if (message.question) formData.append('question', message.question)
        if (message.media?.file)
          formData.append('medias[]', message.media.file)
        messagesToAdd.push(message)
      })

      const loadingMessage = new Message(
        '',
        'received',
        '',
        undefined,
        false,
        'loading'
      )

      // Cancel any in-flight stream before starting a new one
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setChatStarted(true)
      setSubmitting(true)
      setMessages((prev) => [...prev, ...messagesToAdd, loadingMessage])

      const loadingId = loadingMessage.id

      try {
        const result: ChatResponse = ENABLE_SSE_CHAT
          ? await submitChatRequestSSE(
              formData,
              {
                onStatus(statusMessage) {
                  setMessages((prev) =>
                    prev.map((msg) => {
                      if (msg.id !== loadingId) return msg
                      return Object.assign(
                        Object.create(Object.getPrototypeOf(msg)),
                        msg,
                        {
                          thinkingStages: [
                            ...(msg.thinkingStages || []),
                            statusMessage,
                          ],
                        }
                      )
                    })
                  )
                },
                onTextDelta: ENABLE_STREAM_CHAT
                  ? (content) => {
                      streamBufferRef.current += content
                      if (!rafRef.current) {
                        rafRef.current = requestAnimationFrame(() => {
                          const buffered = streamBufferRef.current
                          streamBufferRef.current = ''
                          rafRef.current = 0
                          setMessages((prev) =>
                            prev.map((msg) => {
                              if (msg.id !== loadingId) return msg
                              return Object.assign(
                                Object.create(Object.getPrototypeOf(msg)),
                                msg,
                                {
                                  streamingText:
                                    (msg.streamingText || '') + buffered,
                                }
                              )
                            })
                          )
                        })
                      }
                    }
                  : undefined,
                onTextClear: ENABLE_STREAM_CHAT
                  ? () => {
                      streamBufferRef.current = ''
                      if (rafRef.current) {
                        cancelAnimationFrame(rafRef.current)
                        rafRef.current = 0
                      }
                      setMessages((prev) =>
                        prev.map((msg) => {
                          if (msg.id !== loadingId) return msg
                          return Object.assign(
                            Object.create(Object.getPrototypeOf(msg)),
                            msg,
                            { streamingText: '' }
                          )
                        })
                      )
                    }
                  : undefined,
              },
              controller.signal
            )
          : await submitChatRequest(formData)

        if (result.sessionId) {
          setSessionId(result.sessionId)
          LocalStorageService.setItem('chatSessionId', result.sessionId)
        }
        setSessionMessageCount((count) => {
          const nextCount = count + 1
          if (nextCount >= 10 && isFree) {
            setShowSessionPrompt(true)
          }
          return nextCount
        })
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== loadingId) return msg
            const next = new Message(
              result.message,
              'received',
              undefined,
              undefined,
              undefined,
              'complete',
              undefined,
              undefined,
              result.images,
              result.follow_ups,
              result.sources
            )
            next.serverId = result.message_id
            return next
          })
        )
        queryClient.invalidateQueries({
          queryKey: queryKeys.chat.history(result.sessionId),
        })
      } catch (error: any) {
        // Aborted by user sending a new message — clean up silently
        if (error?.name === 'AbortError' || controller.signal.aborted) {
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== loadingId)
          )
          return
        }
        if (error?.status === 429 || error?.error === 'daily_limit_reached') {
          setLimitReached({ resetsAt: error?.resets_at })
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== loadingId)
          )
        } else {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === loadingId
                ? new Message(
                    "Couldn't get an answer for that one. Please try again.",
                    'received',
                    '',
                    undefined,
                    true,
                    'error'
                  )
                : msg
            )
          )
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
        setSubmitting(false)
      }
    },
    [user, localUser, sessionId, isFree, queryClient]
  )

  // Host-side presence — subscribes to the public walk SSE stream while a
  // walk is active and surfaces the first real guest name it sees. See
  // useWalkPresence for the self-echo filtering rationale.
  const liveGuestName = useWalkPresence(walk?.token ?? null)

  const exposedWalk = useMemo<WalkState | null>(() => {
    if (!walk) return null
    if (!liveGuestName) return walk
    return { ...walk, guestName: liveGuestName }
  }, [walk, liveGuestName])

  return (
    <ChatContext.Provider
      value={{
        messages,
        setMessages,
        chatStarted,
        setChatStarted,
        submitting,
        sessionId,
        setSessionId,
        limitReached,
        setLimitReached,
        showSessionPrompt,
        setShowSessionPrompt,
        sessionMessageCount,
        onSubmitMessages,
        abortCurrentRequest,
        resetChat,
        walk: exposedWalk,
        walkCreating,
        walkError,
        createWalk,
        revokeWalk,
        clearWalkError,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export default ChatProvider
